import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI, setStoredToken } from '../utils/api';

const AuthContext = createContext(null);

const USER_STORAGE_KEY = 'iremind_user';

const readStoredUser = () => {
  try {
    const raw = localStorage.getItem(USER_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

const writeStoredUser = (user) => {
  try {
    if (user) localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
    else localStorage.removeItem(USER_STORAGE_KEY);
  } catch { /* ignore */ }
};

export const AuthProvider = ({ children }) => {
  // Initialise synchronously from localStorage — prevents any flash
  const [user, setUserState] = useState(() => readStoredUser());
  const [loading, setLoading] = useState(true);

  const setUser = useCallback((u) => {
    writeStoredUser(u);
    setUserState(u);
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const data = await authAPI.me();
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const data = await authAPI.login({ email, password });
    if (data?.token) setStoredToken(data.token);
    setUser(data);
    return data;
  };

  const register = async (email, password, name) => {
    const data = await authAPI.register({ email, password, name });
    if (data?.token) setStoredToken(data.token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await authAPI.logout(); } catch { /* ignore */ }
    setStoredToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
